import { useQuery } from '@tanstack/react-query';
import { getRouteApi } from '@tanstack/react-router';
import type { LngLatBounds, LngLatLike } from '@vis.gl/react-maplibre';
import { Layer, Source } from '@vis.gl/react-maplibre';
import type { Feature, FeatureCollection, Point } from 'geojson';
import type { GeoJSONSource, MapLayerEventType } from 'maplibre-gl';
import maplibregl from 'maplibre-gl';
import { useCallback, useMemo, useRef } from 'react';
import z from 'zod';

import clusterIcon from '@/assets/icons/map-markers/cluster.svg?url';
import liveIcon from '@/assets/icons/map-markers/facilities/data.svg?url';
import parkAndRideFacilityIcon from '@/assets/icons/map-markers/facilities/park-and-ride-facility.svg?url';
import parkingFacilityIcon from '@/assets/icons/map-markers/facilities/parking-facility.svg?url';
import pickupParkingFacilityIcon from '@/assets/icons/map-markers/facilities/pickup-parking-facility.svg?url';
import frameCircleIcon from '@/assets/icons/map-markers/frame-circle.svg?url';
import frameRectangleIcon from '@/assets/icons/map-markers/frame-rectangle.svg?url';
import busStationIcon from '@/assets/icons/map-markers/stations/bus-station.svg?url';
import trainStationIcon from '@/assets/icons/map-markers/stations/train-station.svg?url';
import { pickupParkingFacilityFeatureCollection } from '@/data/pickupParkingFacilityFeatureCollection';
import { useMapImages } from '@/hooks/useMapImages';
import { useMapLayerOnEvent } from '@/hooks/useMapLayerOnEvent';
import { useScreenSize } from '@/hooks/useScreenSize';
import { MAP_LAYER_MAPPING } from '@/mappings/MAP_LAYER_MAPPING';
import { MAP_FLY_TO_ZOOM } from '@/modules/MAP_FLY_TO_ZOOM';
import { MAP_MAX_ZOOM } from '@/modules/MAP_MAX_ZOOM';
import { StationType } from '@/modules/api/backend';
import {
  backendApiParkAndRideFacilitiesFindAllOptions,
  backendApiParkingDataFindAllLatestParkAndRideFacilityDataOptions,
  backendApiParkingDataFindAllLatestParkingFacilityDataOptions,
  backendApiParkingFacilitiesFindAllOptions,
  backendApiStationsFindAllOptions,
} from '@/modules/api/backend/@tanstack/react-query.gen';
import { MapLayer, zMapLayer } from '@/types/MapLayer';
import { filterNullable } from '@/utils/filterNullable';
import { getMapLayer } from '@/utils/getMapLayer';

const FRAME_COLORS = {
  HOVER: {
    [MapLayer.PARK_AND_RIDE_FACILITY]: '#0F39934D',
    [MapLayer.PICKUP_PARKING_FACILITY]: '#B916324D',
    [MapLayer.TRAIN_STATION]: '#00754A4D',
    [MapLayer.BUS_STATION]: '#F5AD004D',
  },
  ACTIVE: {
    [MapLayer.PARK_AND_RIDE_FACILITY]: '#0F399380',
    [MapLayer.PICKUP_PARKING_FACILITY]: '#B9163280',
    [MapLayer.TRAIN_STATION]: '#00754A80',
    [MapLayer.BUS_STATION]: '#F5AD0080',
  },
};

const SOURCE = 'stations-and-parking';
const CLUSTER_LAYER = `${SOURCE}-cluster-large`;
const CLUSTER_COUNT_LAYER = `${SOURCE}-cluster-count`;
const ICON_LAYER = `${SOURCE}-icon`;
const LIVE_LAYER = `${SOURCE}-live`;
const FRAME_LAYER = `${SOURCE}-frame`;
const FRAME_CIRCLE = `${SOURCE}-frame-circle`;
const FRAME_RECTANGLE = `${SOURCE}-frame-rectangle`;

const route = getRouteApi('/_public/_map');

export function MapLayers() {
  const { layers, id } = route.useSearch();
  const navigate = route.useNavigate();
  const { isDesktop } = useScreenSize();
  const lastHoverIdRef = useRef<string | number | null>(null);

  const { data: parkingFacilities } = useQuery({
    ...backendApiParkingFacilitiesFindAllOptions({
      query: {
        publication: true,
      },
    }),
    select: (data) =>
      data
        .map(({ geometry, ...properties }) => {
          if (!geometry) return undefined;

          return {
            type: 'Feature',
            geometry,
            properties: {
              ...properties,
              mapLayer: MapLayer.PARKING_FACILITY,
            },
          } satisfies Feature;
        })
        .filter(filterNullable),
  });

  const { data: parkAndRideFacilities } = useQuery({
    ...backendApiParkAndRideFacilitiesFindAllOptions({
      query: {
        publication: true,
        stationless: false,
      },
    }),
    select: (data) =>
      data
        .map(({ geometry, ...properties }) => {
          if (!geometry) return undefined;

          return {
            type: 'Feature',
            geometry,
            properties: {
              ...properties,
              mapLayer: MapLayer.PARK_AND_RIDE_FACILITY,
            },
          } satisfies Feature;
        })
        .filter(filterNullable),
  });

  const { data: stationlessParkAndRideFacilities } = useQuery({
    ...backendApiParkAndRideFacilitiesFindAllOptions({
      query: {
        publication: true,
        stationless: true,
      },
    }),
    select: (data) =>
      data
        .map(({ geometry, ...properties }) => {
          if (!geometry) return undefined;

          return {
            type: 'Feature',
            geometry,
            properties: {
              ...properties,
              mapLayer: MapLayer.PARKING_FACILITY,
              stationless: true,
            },
          } satisfies Feature;
        })
        .filter(filterNullable),
  });

  const { data: stations } = useQuery(
    backendApiStationsFindAllOptions({
      query: {
        parkAndRideFacilitiesPublication: true,
      },
    }),
  );

  const { data: parkingFacilityWithDataIds } = useQuery({
    ...backendApiParkingDataFindAllLatestParkingFacilityDataOptions(),
    select: (data) => data.map((item) => item.id),
  });

  const { data: parkAndRideFacilityWithDataIds } = useQuery({
    ...backendApiParkingDataFindAllLatestParkAndRideFacilityDataOptions(),
    select: (data) => data.map((item) => item.id),
  });

  const facilitiesWithDataIds = useMemo(() => {
    const parkingFacilityIds = parkingFacilityWithDataIds ?? [];
    const parkAndRideFacilityIds = parkAndRideFacilityWithDataIds ?? [];

    return [...parkingFacilityIds, ...parkAndRideFacilityIds];
  }, [parkingFacilityWithDataIds, parkAndRideFacilityWithDataIds]);

  const stationFeatures = useMemo(
    () =>
      (stations ?? [])
        .map((station) => {
          const { geometry, ...properties } = station;

          return geometry
            ? ({
                type: 'Feature',
                geometry,
                properties: {
                  ...properties,
                  mapLayer: getMapLayer(station),
                },
              } satisfies Feature)
            : null;
        })
        .filter(filterNullable),
    [stations],
  );

  const pickupParkingFacilityFeatures = useMemo(
    () =>
      (pickupParkingFacilityFeatureCollection.features ?? []).map((feature) => ({
        ...feature,
        properties: {
          ...feature.properties,
          mapLayer: MapLayer.PICKUP_PARKING_FACILITY,
        },
      })),
    [],
  );

  const data = useMemo(
    (): FeatureCollection => ({
      type: 'FeatureCollection',
      features: [
        ...(layers?.includes(MapLayer.PARKING_FACILITY)
          ? [...(parkingFacilities ?? []), ...(stationlessParkAndRideFacilities ?? [])]
          : []),
        ...(parkAndRideFacilities && layers?.includes(MapLayer.PARK_AND_RIDE_FACILITY) ? parkAndRideFacilities : []),
        ...(layers?.includes(MapLayer.PICKUP_PARKING_FACILITY) ? pickupParkingFacilityFeatures : []),
        ...(layers?.includes(MapLayer.TRAIN_STATION)
          ? stationFeatures.filter((feature) => feature.properties.type === StationType.TRAIN)
          : []),
        ...(layers?.includes(MapLayer.BUS_STATION)
          ? stationFeatures.filter((feature) => feature.properties.type === StationType.BUS)
          : []),
      ],
    }),
    [
      parkingFacilities,
      stationlessParkAndRideFacilities,
      layers,
      parkAndRideFacilities,
      pickupParkingFacilityFeatures,
      stationFeatures,
    ],
  );

  useMapImages({
    images: [
      { name: CLUSTER_LAYER, url: clusterIcon, width: 32, height: 32 },
      { name: LIVE_LAYER, url: liveIcon, width: 12, height: 12 },

      { name: MapLayer.PARKING_FACILITY, url: parkingFacilityIcon, width: 32, height: 32 },
      { name: MapLayer.PARK_AND_RIDE_FACILITY, url: parkAndRideFacilityIcon, width: 32, height: 32 },
      { name: MapLayer.PICKUP_PARKING_FACILITY, url: pickupParkingFacilityIcon, width: 32, height: 32 },

      { name: MapLayer.TRAIN_STATION, url: trainStationIcon, width: 24, height: 24 },
      { name: MapLayer.BUS_STATION, url: busStationIcon, width: 24, height: 24 },

      { name: FRAME_RECTANGLE, url: frameRectangleIcon, width: 40, height: 40, options: { sdf: true } },
      { name: FRAME_CIRCLE, url: frameCircleIcon, width: 32, height: 32, options: { sdf: true } },
    ],
  });

  const handleMapClick = useCallback(
    ({ target, features: eventFeatures }: MapLayerEventType['click']) => {
      const feature = eventFeatures?.[0];

      if (feature?.geometry.type === 'Point' && feature?.properties?.id != null) {
        const mapLayer = zMapLayer.parse(feature.properties.mapLayer);
        const featureId = z.string().parse(String(feature.properties.id));

        navigate({
          search: (search) => ({
            ...search,
            type: feature.properties.stationless
              ? MAP_LAYER_MAPPING.PARK_AND_RIDE_FACILITY.featureType
              : MAP_LAYER_MAPPING[mapLayer].featureType,
            id: featureId,
          }),
        });

        const [lng, lat] = feature.geometry.coordinates || [];
        if (lng !== undefined && lat !== undefined) {
          target.flyTo({
            center: [lng, lat],
            offset: [0, isDesktop ? 0 : -175],
            ...(target.getZoom() < MAP_FLY_TO_ZOOM - 2 && { zoom: MAP_FLY_TO_ZOOM - 2 }),
          });
        }
      }
    },
    [navigate, isDesktop],
  );

  useMapLayerOnEvent({
    type: 'click',
    layer: ICON_LAYER,
    listener: handleMapClick,
  });

  const handleClusterClick = useCallback(
    async ({ target, features: eventFeatures }: MapLayerEventType['click']) => {
      const clusterId = eventFeatures?.[0]?.properties?.cluster_id;

      const leaves = await (target.getSource(SOURCE) as GeoJSONSource).getClusterLeaves(clusterId, 1000000, 0);

      const bounds = leaves.reduce<LngLatBounds>(
        (currentBounds, feature) => currentBounds.extend((feature.geometry as Point).coordinates as LngLatLike),
        new maplibregl.LngLatBounds(),
      );

      target.fitBounds(bounds, {
        padding: {
          top: isDesktop ? 146 : 128,
          right: isDesktop ? 64 : 32,
          bottom: isDesktop ? 88 : 64,
          left: isDesktop ? 64 : 32,
        },
      });
    },
    [isDesktop],
  );

  useMapLayerOnEvent({
    type: 'click',
    layer: CLUSTER_LAYER,
    listener: handleClusterClick,
  });

  useMapLayerOnEvent({
    type: 'mousemove',
    layer: ICON_LAYER,
    listener: ({ target, features: eventFeatures }) => {
      const map = target;
      const f = eventFeatures?.[0];
      if (!f || f.geometry?.type !== 'Point' || f.properties?.id == null) return;

      const prev = lastHoverIdRef.current;
      if (prev && prev !== f.properties.id) {
        map.setFeatureState({ source: SOURCE, id: prev }, { hover: false });
      }
      lastHoverIdRef.current = f.properties.id;
      map.setFeatureState({ source: SOURCE, id: f.properties.id }, { hover: true });
      map.getCanvas().style.cursor = 'pointer';
    },
  });

  useMapLayerOnEvent({
    type: 'mouseleave',
    layer: ICON_LAYER,
    listener: ({ target }) => {
      const map = target;
      const prev = lastHoverIdRef.current;
      if (prev) {
        map.setFeatureState({ source: SOURCE, id: prev }, { hover: false });
        lastHoverIdRef.current = null;
      }
      map.getCanvas().style.cursor = '';
    },
  });

  return (
    <Source
      cluster
      clusterMaxZoom={MAP_MAX_ZOOM - 2}
      clusterRadius={48}
      data={data}
      id={SOURCE}
      promoteId="id"
      type="geojson"
    >
      <Layer
        filter={['has', 'point_count']}
        id={CLUSTER_LAYER}
        type="symbol"
        layout={{
          'icon-image': CLUSTER_LAYER,
          'icon-allow-overlap': true,
          'icon-anchor': 'center',
        }}
      />

      <Layer
        filter={['has', 'point_count']}
        id={CLUSTER_COUNT_LAYER}
        type="symbol"
        layout={{
          'text-allow-overlap': true,
          'text-field': '{point_count_abbreviated}',
          'text-size': 14,
          'text-font': ['Noto Sans Bold'],
        }}
      />

      <Layer
        filter={['!has', 'point_count']}
        id={ICON_LAYER}
        type="symbol"
        layout={{
          'icon-image': ['get', 'mapLayer'],
          'icon-allow-overlap': true,
          'icon-size': 1,
        }}
      />

      <Layer
        filter={['all', ['!has', 'point_count'], ['in', 'id', ...(facilitiesWithDataIds ?? [])]]}
        id={LIVE_LAYER}
        type="symbol"
        layout={{
          'icon-image': LIVE_LAYER,
          'icon-allow-overlap': true,
          'icon-offset': [14, -14],
        }}
      />

      <Layer
        beforeId={ICON_LAYER}
        id={FRAME_LAYER}
        type="symbol"
        layout={{
          'icon-image': [
            'match',
            ['get', 'mapLayer'],

            MapLayer.PARK_AND_RIDE_FACILITY,
            FRAME_RECTANGLE,

            MapLayer.PICKUP_PARKING_FACILITY,
            FRAME_RECTANGLE,

            MapLayer.TRAIN_STATION,
            FRAME_CIRCLE,

            MapLayer.BUS_STATION,
            FRAME_CIRCLE,

            'none',
          ],

          'icon-allow-overlap': true,
          'icon-anchor': 'center',
          'icon-size': 1,
        }}
        paint={{
          'icon-color': [
            'let',
            'isActive',
            ['==', ['to-string', ['get', 'id']], id ?? ''],

            'isHover',
            ['boolean', ['feature-state', 'hover'], false],

            [
              'match',
              ['get', 'mapLayer'],

              MapLayer.PARK_AND_RIDE_FACILITY,
              [
                'case',
                ['var', 'isActive'],
                FRAME_COLORS.ACTIVE[MapLayer.PARK_AND_RIDE_FACILITY],
                ['var', 'isHover'],
                FRAME_COLORS.HOVER[MapLayer.PARK_AND_RIDE_FACILITY],
                'rgba(0,0,0,0)',
              ],

              MapLayer.PICKUP_PARKING_FACILITY,
              [
                'case',
                ['var', 'isActive'],
                FRAME_COLORS.ACTIVE[MapLayer.PICKUP_PARKING_FACILITY],
                ['var', 'isHover'],
                FRAME_COLORS.HOVER[MapLayer.PICKUP_PARKING_FACILITY],
                'rgba(0,0,0,0)',
              ],

              MapLayer.TRAIN_STATION,
              [
                'case',
                ['var', 'isActive'],
                FRAME_COLORS.ACTIVE[MapLayer.TRAIN_STATION],
                ['var', 'isHover'],
                FRAME_COLORS.HOVER[MapLayer.TRAIN_STATION],
                'rgba(0,0,0,0)',
              ],

              MapLayer.BUS_STATION,
              [
                'case',
                ['var', 'isActive'],
                FRAME_COLORS.ACTIVE[MapLayer.BUS_STATION],
                ['var', 'isHover'],
                FRAME_COLORS.HOVER[MapLayer.BUS_STATION],
                'rgba(0,0,0,0)',
              ],

              'rgba(0,0,0,0)',
            ],
          ],
        }}
      />
    </Source>
  );
}
